import { track } from '@cartwheel/analytics'
import { type CalendarAppointment, useCalendarAppointmentVideoLinkQuery } from '@cartwheel/api/v1'
import { useEffect, useId, useRef } from 'react'
import { formatTime } from './utils'

interface AppointmentDetailModalProps {
  appointment: CalendarAppointment
  onClose: () => void
  trackingProps?: Record<string, unknown>
}

export function AppointmentDetailModal({
  appointment,
  onClose,
  trackingProps = {},
}: AppointmentDetailModalProps) {
  const titleId = useId()
  const dialogRef = useRef<HTMLDivElement>(null)

  const { data: videoData, isLoading: isVideoLoading } = useCalendarAppointmentVideoLinkQuery({
    path: { calendar_appointment_id: appointment.id },
  })

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    dialogRef.current?.focus()
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      {/* Backdrop close button - covers the overlay behind the dialog */}
      <button
        type="button"
        onClick={onClose}
        className="absolute inset-0 cursor-default"
        aria-label="Close dialog"
        tabIndex={-1}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="relative bg-white rounded-lg shadow-xl p-6 w-full max-w-sm mx-4 outline-none"
      >
        <div className="flex justify-between items-start mb-4">
          <h3 id={titleId} className="text-base font-semibold text-gray-900">
            Appointment Details
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-600 text-lg leading-none"
          >
            ✕
          </button>
        </div>

        <dl className="space-y-2 text-sm">
          <div>
            <dt className="text-gray-500">Student</dt>
            <dd className="font-medium text-gray-900">
              {appointment.student_first_name} {appointment.student_last_name}
            </dd>
          </div>

          <div>
            <dt className="text-gray-500">Profile</dt>
            <dd>
              <a
                href={`/students/${appointment.student_id}?section=appointments`}
                className="text-forest-600 hover:underline"
                onClick={() =>
                  track('CALENDAR_STUDENT_PROFILE_CLICKED', {
                    ...trackingProps,
                    appointmentId: appointment.id,
                    clientId: appointment.student_id,
                  })
                }
              >
                View appointments →
              </a>
            </dd>
          </div>

          {appointment.clinician_name && (
            <div>
              <dt className="text-gray-500">Clinician</dt>
              <dd className="font-medium text-gray-900">{appointment.clinician_name}</dd>
            </div>
          )}

          {appointment.appointment_type_name && (
            <div>
              <dt className="text-gray-500">Type</dt>
              <dd className="text-gray-700">{appointment.appointment_type_name}</dd>
            </div>
          )}

          <div>
            <dt className="text-gray-500">Time</dt>
            <dd className="text-gray-700">{formatTime(appointment.start_time)}</dd>
          </div>

          <div>
            <dt className="text-gray-500">Video Link</dt>
            <dd>
              {isVideoLoading ? (
                <span className="text-gray-400">Loading...</span>
              ) : videoData?.video_url ? (
                <a
                  href={videoData.video_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-forest-600 hover:underline"
                  onClick={() =>
                    track('CALENDAR_VIDEO_LINK_CLICKED', {
                      ...trackingProps,
                      appointmentId: appointment.id,
                    })
                  }
                >
                  Join video session →
                </a>
              ) : (
                <span className="text-gray-400">No video link available</span>
              )}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  )
}
